import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { InputNumberProps } from "antd/es/input-number";
import pick from "lodash-es/pick";
import { defineMessages, useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import {
  EntityTypesSupportingBulkEdit,
  EntityTypesSupportingRelationshipBulkEdit,
  EntityTypeSupportingGroups,
  Group,
  Trip,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import FunnelStageValue from "@mapmycustomers/shared/types/FunnelStageValue";
import User from "@mapmycustomers/shared/types/User";
import {
  Labeled,
  NumberField,
  SelectField,
  TextAreaWithMentions,
  TextField,
} from "@mapmycustomers/ui";

import FrequencyIntervalEditor from "@app/component/frequency/FrequencyIntervalEditor";
import FrequencyIntervalValue from "@app/component/frequency/type/FrequencyIntervalValue";
import GroupsSelection from "@app/component/GroupsSelection";
import AddressField from "@app/component/input/Address/AddressField";
import DatePicker from "@app/component/input/DatePicker";
import DealLossReasonSelectField from "@app/component/input/DealLossReasonSelectField";
import UserSelectField from "@app/component/input/UserSelectField";
import { getActivityTypes } from "@app/store/activity";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { MAX_DEAL_SCORE } from "@app/util/consts";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import { parseApiDate } from "@app/util/parsers";
import useActivityTypeOptions from "@app/util/ui/useActivityTypeOptions";

import ColorField from "./ColorField";
import CountryField from "./CountryField";
import FunnelControl from "./FunnelField";
import styles from "./GeneralFieldsEditor.module.scss";
import RelationshipField, { EntitiesSupportingRelationshipBulkEdit } from "./RelationshipField";
import TripTypeField from "./TripTypeField";

interface GeneralFieldsEditorProps {
  activityTypes: ActivityType[];
  disabled?: boolean;
  disableLabel?: boolean;
  entityType: EntityTypesSupportingBulkEdit;
  field: IField;
  groups?: Group[];
  isStageRequired?: boolean;
  onChange: (value?: any) => void;
  onChangePrimaryEntityId?: (id: number) => void;
  showFrequencyIntervalActivityTypes?: boolean;
  users: User[];
  value?: unknown;
}

const messages = defineMessages({
  changeTo: {
    id: "view.selectBar.button.bulkEdit.panel.changeTo",
    defaultMessage: "Change {value} to",
    description: "Bulk Edit change to control label",
  },
  placeholder: {
    id: "view.selectBar.button.bulkEdit.panel.placeholder",
    defaultMessage: "Enter value",
    description: "Enter value placeholder",
  },
});

interface OverrideInputProps {
  [key: string]: InputNumberProps;
}

const constraints: OverrideInputProps = {
  [CompanyFieldName.ANNUAL_REVENUE]: {
    precision: 2,
  },

  [CompanyFieldName.NUM_EMPLOYEES]: {
    min: 1,
    precision: 0,
  },

  [DealFieldName.AMOUNT]: {
    min: 0,
    precision: 2,
  },

  [DealFieldName.SCORE]: {
    max: MAX_DEAL_SCORE,
    min: 0,
    precision: 0,
  },
};

const GeneralFieldsEditor: React.FC<GeneralFieldsEditorProps> = ({
  activityTypes,
  disabled,
  disableLabel,
  entityType,
  field,
  groups,
  isStageRequired,
  onChange,
  onChangePrimaryEntityId,
  showFrequencyIntervalActivityTypes,
  users,
  value,
}) => {
  const intl = useIntl();
  const [numericValue, setNumericValue] = useState<number | undefined>();

  const isFunnelControl = field.hasFeature(FieldFeature.FUNNEL_FIELD);
  const isActivityTypeControl = field.hasFeature(FieldFeature.ACTIVITY_TYPE_FIELD);
  const isGroupControl = field.hasFeature(FieldFeature.GROUP_FIELD);
  const isFrequencyInterval = field.hasFeature(FieldFeature.FREQUENCY_INTERVAL);
  const isDealLossReason = field.name === DealFieldName.DEAL_LOSS_REASON;
  const isNoteField = field.name === ActivityFieldName.NOTE;
  const isTripTypeField = field.name === TripFieldName.TYPE;
  const isRelationshipField = field.hasAllFeatures(FieldFeature.RELATIONSHIPS);

  const activityTypeOptions = useActivityTypeOptions(
    activityTypes,
    value as ActivityType["id"],
    useCallback((type) => !type.active, [])
  );

  const useFallbackControls = !(
    isActivityTypeControl ||
    isDealLossReason ||
    isFrequencyInterval ||
    isFunnelControl ||
    isNoteField ||
    isRelationshipField ||
    isTripTypeField ||
    field.hasAnyFeature(
      FieldFeature.COUNTRY_FIELD,
      FieldFeature.BULK_ADDRESS,
      FieldFeature.USES_COLORS,
      FieldFeature.OWNER_FIELD
    )
  );

  const handleUserChange = useCallback(
    (user: undefined | User) => onChange(pick(user, "id")),
    [onChange]
  );

  const handleNumericChange = useCallback<any>(
    (value?: number) => {
      setNumericValue(value);
      onChange(value);
    },
    [onChange, setNumericValue]
  );

  useEffect(() => {
    if (field.type === FieldType.NUMBER) {
      setNumericValue(value as number);
    }
  }, [field.type, value]);

  const label = disableLabel
    ? undefined
    : intl.formatMessage(messages.changeTo, { value: field.displayName });

  return (
    <>
      {isRelationshipField && (
        <RelationshipField
          entityType={entityType as EntityTypesSupportingRelationshipBulkEdit}
          field={field}
          onChange={onChange}
          onChangePrimaryEntityId={onChangePrimaryEntityId}
          value={value as EntitiesSupportingRelationshipBulkEdit}
        />
      )}

      {isFunnelControl && (
        <FunnelControl
          disabled={disabled}
          isStageRequired={isStageRequired}
          onChange={onChange}
          value={value as Partial<FunnelStageValue> | undefined}
        />
      )}

      {isGroupControl && groups && (
        <GroupsSelection
          className={styles.fullWidth}
          disabled={disabled}
          entityType={entityType as EntityTypeSupportingGroups}
          groups={groups}
          onChange={onChange}
          selectedGroupIds={new Set(value as Group["id"][])}
        />
      )}

      {isDealLossReason && (
        <DealLossReasonSelectField
          className={styles.fullWidth}
          disabled={disabled}
          onChange={onChange}
          value={value as DealLossReason["id"]}
        />
      )}

      {isFrequencyInterval && (
        <FrequencyIntervalEditor
          disabled={disabled}
          onChange={onChange}
          showActivityTypes={showFrequencyIntervalActivityTypes}
          value={value as FrequencyIntervalValue | undefined}
        />
      )}

      {isActivityTypeControl && activityTypeOptions && (
        <Labeled label={label}>
          <SelectField<ActivityType["id"]>
            className={styles.fullWidth}
            disabled={disabled}
            onChange={onChange}
            options={activityTypeOptions}
            placeholder={"Select Activity Type"}
            tabIndex={-1}
            value={value as ActivityType["id"]}
          />
        </Labeled>
      )}

      {field.hasFeature(FieldFeature.BULK_ADDRESS) && (
        <AddressField
          disabled={disabled}
          label={intl.formatMessage({
            id: "view.selectBar.button.bulkEdit.panel.selectAddress",
            defaultMessage: "Change Address to",
            description: "Bulk Edit Address control label",
          })}
          onChange={onChange}
        />
      )}

      {field.hasFeature(FieldFeature.USES_COLORS) && (
        <Labeled
          label={
            disableLabel
              ? undefined
              : intl.formatMessage({
                  id: "view.selectBar.button.bulkEdit.panel.selectColor",
                  defaultMessage: "Set pin color",
                  description: "Bulk Edit color control label",
                })
          }
        >
          <ColorField disabled={disabled} onChange={onChange} value={value as AnyColor} />
        </Labeled>
      )}

      {field.hasFeature(FieldFeature.COUNTRY_FIELD) && (
        <CountryField disabled={disabled} onChange={onChange} />
      )}

      {field.hasFeature(FieldFeature.OWNER_FIELD) && (
        <UserSelectField
          className={styles.fullWidth}
          defaultUsersOption={false}
          disabled={disabled}
          label={label}
          onChange={handleUserChange}
          placeholder={intl.formatMessage({
            id: "view.selectBar.button.bulkEdit.panel.owner.placeholder",
            defaultMessage: "Select owner",
            description: "Select owner placeholder",
          })}
          users={users}
        />
      )}

      {isNoteField && (
        <Labeled className={styles.fullWidth} label={label}>
          <TextAreaWithMentions
            allUsers={users}
            className={styles.fullWidth}
            disabled={disabled}
            onChange={onChange}
            placeholder={intl.formatMessage(messages.placeholder)}
            value={value as string}
          />
        </Labeled>
      )}

      {useFallbackControls && (
        <>
          {field.type === FieldType.NUMBER && (
            <NumberField
              disabled={disabled}
              inputClassName={styles.fullWidth}
              label={label}
              min={0}
              onChange={handleNumericChange}
              placeholder={intl.formatMessage(messages.placeholder)}
              value={numericValue}
              {...constraints[field.name]}
            />
          )}

          {field.type === FieldType.STRING && (
            <TextField
              className={styles.fullWidth}
              disabled={disabled}
              label={label}
              onChange={onChange}
              placeholder={intl.formatMessage(messages.placeholder)}
              value={value as string}
            />
          )}

          {field.type === FieldType.DATE_TIME && (
            <Labeled label={label}>
              <DatePicker
                allowClear={false}
                className={styles.fullWidth}
                disabled={disabled}
                format="PP"
                onChange={onChange}
                value={value ? parseApiDate(value as Date) : undefined}
              />
            </Labeled>
          )}
        </>
      )}

      {isTripTypeField && (
        <TripTypeField disabled={disabled} onChange={onChange} value={value as Trip["type"]} />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(GeneralFieldsEditor);
